import * as React from 'react';
import cx from 'classnames';
import { zipWith } from 'lodash';
import { Tag, TagColor, TagSize } from '@app/common/components/tag/Tag';
import { getTagColour } from '@app/containers/dashboard/documents/FlkAPdf/utils';
import { isUploadedDocument, LocalDocument, UploadedDocument } from '@app/types/UploadADoc';
import styles from './ClientAnnotationCounts.module.scss';

interface ClientAnnotationCountsProps {
    document: UploadedDocument | LocalDocument;
    clients: unknown[];
}

const ClientAnnotationCounts: React.FC<ClientAnnotationCountsProps> = ({ document, clients }) => {
    const getParsedAnnotationCounts = (): number[] => {
        try {
            if (!isUploadedDocument(document) || !document.annotationsCount) {
                return [];
            }
            // Transform from object with shape {"clientIndex": annotationCount} to array with index corresponding to client position
            // and value corresponding to annotation count for that client. Uses Object.assign() so that keys are converted to indices to leave space
            // for clients without annotations, as these are not present in the raw object.
            return Object.assign([], JSON.parse(document.annotationsCount));
        } catch (e) {
            return [];
        }
    };

    // Zip the arrays together to handle clients without annotations (i.e. clients.length > annotationCounts.length)
    // clientAnnotationsCounts.length = max(clients.length, annotationCounts.length)
    const clientAnnotationCounts = zipWith(
        clients,
        getParsedAnnotationCounts(),
        (_client, annotationCount) => annotationCount ?? 0
    ).slice(0, clients.length);

    return clientAnnotationCounts.some(count => count > 0) ? (
        <div className={styles.countsContainer}>
            {clientAnnotationCounts.map((signatureCount, clientIndex) => (
                <Tag
                    className={cx(styles.count, {
                        [styles.singleDigit]: signatureCount < 10
                    })}
                    text={signatureCount > 0 ? signatureCount.toString() : ''}
                    size={TagSize.Small}
                    color={signatureCount > 0 ? getTagColour(clientIndex) : TagColor.Grey}
                    key={clientIndex}
                />
            ))}
        </div>
    ) : (
        <div className={styles.noSignatures}>No Signature Placeholders</div>
    );
};

export default ClientAnnotationCounts;
